// src/roles/store/SiteTransfersPending.jsx
//
// Site-wise Stock Tracking — Phase 2 (site-to-site transfer).
// Action queue for site_stock_transfers docs in "pending_acceptance".
// Sender's stock already deducted at creation time (see
// createSiteStockTransfer() in inventoryService.js) — this page is
// where a transfer gets resolved: Accept (credits the receiving site),
// Reject (receiver declines — reverses sender's stock back), or Cancel
// (aborts — same reversal as Reject). No per-site user restriction in
// this phase — any user in the sm/owner/manager/store tier can act on
// any site's pending transfer, same as how any of them can approve an
// MRF for any site today.

import { useCallback, useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import {
  acceptSiteStockTransfer,
  rejectSiteStockTransfer,
  cancelSiteStockTransfer,
} from "../../services/inventoryService";

export default function SiteTransfersPending() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const uid        = user.uid || user.id || "";
  const userName   = user.name || "";

  const [transfers, setTransfers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [processingId, setProcessingId] = useState(null);

  // Reason modal — shared for Reject + Cancel (same required-reason shape)
  const [reasonModal, setReasonModal] = useState(null); // { transferId, action: "reject" | "cancel" }
  const [reasonText,  setReasonText]  = useState("");
  const [submittingReason, setSubmittingReason] = useState(false);

  const loadTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const snap = await getDocs(query(
        collection(db, "site_stock_transfers"),
        where("company_id", "==", company_id),
        where("status",     "==", "pending_acceptance")
      ));
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setTransfers(list);
    } catch (err) {
      console.error("SiteTransfersPending — load error:", err);
      setError(err.message || "Failed to load pending transfers");
    } finally {
      setLoading(false);
    }
  }, [company_id]);

  useEffect(() => { loadTransfers(); }, [loadTransfers]);

  const handleAccept = async (transfer) => {
    if (!window.confirm(
      `Accept ${transfer.qty} ${transfer.unit} of ${transfer.item_name} into ${transfer.to_site_name}?`
    )) return;
    try {
      setProcessingId(transfer.id);
      setError(""); setSuccess("");
      await acceptSiteStockTransfer({
        transfer_id:      transfer.id,
        accepted_by:      uid,
        accepted_by_name: userName,
        company_id,
      });
      setSuccess(`Transfer ${transfer.transfer_no} accepted — stock credited to ${transfer.to_site_name}.`);
      await loadTransfers();
    } catch (err) {
      setError(err.message || "Failed to accept transfer");
    } finally {
      setProcessingId(null);
    }
  };

  const openReasonModal = (transferId, action) => {
    setReasonModal({ transferId, action });
    setReasonText("");
  };

  const closeReasonModal = () => {
    setReasonModal(null);
    setReasonText("");
  };

  const submitReason = async () => {
    if (!reasonText.trim()) { alert("Reason is mandatory."); return; }
    const { transferId, action } = reasonModal;
    try {
      setSubmittingReason(true);
      setError(""); setSuccess("");

      if (action === "reject") {
        await rejectSiteStockTransfer({
          transfer_id:      transferId,
          rejected_by:      uid,
          rejected_by_name: userName,
          rejection_reason: reasonText.trim(),
          company_id,
        });
        setSuccess("Transfer rejected — stock reversed back to source site.");
      } else {
        await cancelSiteStockTransfer({
          transfer_id:         transferId,
          cancelled_by:        uid,
          cancelled_by_name:   userName,
          cancellation_reason: reasonText.trim(),
          company_id,
        });
        setSuccess("Transfer cancelled — stock reversed back to source site.");
      }

      closeReasonModal();
      await loadTransfers();
    } catch (err) {
      setError(err.message || "Failed to process request");
    } finally {
      setSubmittingReason(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts?.seconds) return "-";
    try { return new Date(ts.seconds * 1000).toLocaleString(); }
    catch { return "-"; }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading pending transfers...</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Site Transfers — Pending</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Site-to-site material transfers awaiting acceptance
        </p>
      </div>

      {error   && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">{success}</div>}

      {transfers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
          <div className="text-4xl mb-2">✅</div>
          <p>No pending site transfers.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(t => (
            <div key={t.id} className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-amber-400">
              <div className="flex justify-between items-start gap-3 flex-wrap mb-3">
                <div>
                  <div className="font-semibold text-slate-800">{t.item_name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{t.transfer_no}</div>
                </div>
                <span className="bg-amber-50 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  ⏳ Pending Acceptance
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4">
                <div>
                  <div className="text-gray-400">From Site</div>
                  <div className="font-medium text-slate-700">{t.from_site_name}</div>
                </div>
                <div>
                  <div className="text-gray-400">To Site</div>
                  <div className="font-medium text-slate-700">{t.to_site_name}</div>
                </div>
                <div>
                  <div className="text-gray-400">Qty</div>
                  <div className="font-medium text-slate-700">{t.qty} {t.unit}</div>
                </div>
                <div>
                  <div className="text-gray-400">Requested By</div>
                  <div className="font-medium text-slate-700">{t.requested_by_name || "-"}</div>
                </div>
                <div>
                  <div className="text-gray-400">Created</div>
                  <div className="font-medium text-slate-700">{formatDate(t.created_at)}</div>
                </div>
                {t.remarks && (
                  <div className="col-span-2">
                    <div className="text-gray-400">Remarks</div>
                    <div className="font-medium text-slate-700">{t.remarks}</div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleAccept(t)}
                  disabled={processingId === t.id}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {processingId === t.id ? "Processing..." : "✓ Accept"}
                </button>
                <button
                  onClick={() => openReasonModal(t.id, "reject")}
                  disabled={processingId === t.id}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50"
                >
                  ✕ Reject
                </button>
                <button
                  onClick={() => openReasonModal(t.id, "cancel")}
                  disabled={processingId === t.id}
                  className="px-4 py-2 rounded-lg bg-gray-500 text-white text-xs font-semibold hover:bg-gray-600 disabled:opacity-50"
                >
                  Cancel Transfer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── REASON MODAL — shared for Reject + Cancel ── */}
      {reasonModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <h3 className="font-bold text-gray-800 mb-1">
              {reasonModal.action === "reject" ? "✕ Reject Transfer" : "Cancel Transfer"}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Stock will be reversed back to the source site. Reason is mandatory.
            </p>
            <textarea
              rows={3}
              value={reasonText}
              onChange={e => setReasonText(e.target.value)}
              placeholder="Why?"
              autoFocus
              className="w-full border rounded-xl px-3 py-2 text-sm outline-none resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={closeReasonModal}
                className="flex-1 px-4 py-2 rounded-xl border text-sm font-semibold text-gray-600"
              >
                ← Go Back
              </button>
              <button
                onClick={submitReason}
                disabled={submittingReason || !reasonText.trim()}
                className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {submittingReason ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}